import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Search, Sparkles, AlertTriangle, XCircle, FileBadge, Truck, Plus, Clock, DollarSign, RefreshCw, CheckCircle2, Bell, FileText, Download, History as HistoryIcon } from "lucide-react";
import AdminOrder360Drawer from "../../components/AdminOrder360Drawer";

// Admin oversight: every order, every invoice, all sellers — single tables.
// Use the tab pill to switch. Drives admin's "I need to find that one record" workflow.
//
// Iter-84 (May 2026): added Manual orders / Prebook activity / Restocking
// fees sub-tabs + bulk-action toolbar on the Orders tab + PO/Invoice/Pack
// PDF download buttons.
const TABS = [
  { k: "orders",     l: "Orders" },
  { k: "invoices",   l: "Invoices" },
  { k: "sellers",    l: "Sellers" },
  { k: "dropship",   l: "Dropship orders" },
  { k: "manual",     l: "Manual" },
  { k: "prebook",    l: "Prebook activity" },
  { k: "restocking", l: "Restocking fees" },
  { k: "cancellations", l: "Buyer cancellations" },
  { k: "ship-changes",  l: "Shipping changes" },
  { k: "ext-pos",       l: "External POs" },
  { k: "ext-invoices",  l: "External invoices" },
  { k: "po-lifecycle",  l: "PO lifecycle" },
  { k: "supplier-claims", l: "Supplier claims" },
];

export default function AdminCommerce() {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [dropship, setDropship] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    api.get("/admin/orders").then(r => setOrders(r.data));
    api.get("/admin/invoices").then(r => setInvoices(r.data));
    api.get("/admin/sellers").then(r => setSellers(r.data));
    api.get("/admin/dropship-orders").then(r => setDropship(r.data));
  }, []);

  const data = { orders, invoices, sellers, dropship }[tab];
  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(d => JSON.stringify(d).toLowerCase().includes(q));
  }, [data, search]);

  return (
    <>
      <PageHeader title="Commerce · Oversight"
        subtitle={`${orders.length} orders · ${invoices.length} invoices · ${sellers.length} sellers · ${dropship.length} dropship orders · click any order row for full state`}/>
      <div className="p-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-1.5 flex-wrap" data-testid="commerce-tabs">
            {TABS.map(t => {
              const count = { orders, invoices, sellers, dropship }[t.k]?.length;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${tab === t.k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}>
                  {t.l}
                  {count !== undefined && (
                    <span className={`text-[10px] mono ${tab === t.k ? "text-background/70" : "text-foreground/45"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…" data-testid="commerce-search"
              className="pl-9 h-10 w-72"/>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          {tab === "orders"     && <OrdersTable rows={filtered} onOpen={setSelectedOrder}/>}
          {tab === "invoices"   && <InvoicesTable rows={filtered}/>}
          {tab === "sellers"    && <SellersTable rows={filtered}/>}
          {tab === "dropship"   && <DropshipTable rows={filtered}/>}
        </div>
        {tab === "orders"     && <BulkActionsBar/>}
        {tab === "manual"     && <ManualOrdersTab onOpen={setSelectedOrder}/>}
        {tab === "prebook"    && <PrebookActivityTab/>}
        {tab === "restocking" && <RestockingFeesTab onOpen={setSelectedOrder}/>}
        {tab === "cancellations" && <BuyerCancellationsTab onOpen={setSelectedOrder}/>}
        {tab === "ship-changes"  && <ShippingChangesTab onOpen={setSelectedOrder}/>}
        {tab === "ext-pos"       && <ExternalPosTab/>}
        {tab === "ext-invoices"  && <ExternalInvoicesTab/>}
        {tab === "po-lifecycle"  && <PoLifecycleTab/>}
        {tab === "supplier-claims" && <SupplierClaimsTab/>}
      </div>
      <AdminOrder360Drawer order={selectedOrder} onClose={() => setSelectedOrder(null)}/>
    </>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 font-medium">{children}</th>;
}

function StatusChip({ s }) {
  const m = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    processing: "bg-blue-100 text-blue-800 border-blue-200",
    transit: "bg-indigo-100 text-indigo-800 border-indigo-200",
    delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    overdue: "bg-rose-100 text-rose-800 border-rose-200",
    sent: "bg-amber-100 text-amber-800 border-amber-200",
    draft: "bg-secondary border-border",
    forwarded: "bg-amber-100 text-amber-800 border-amber-200",
    shipped: "bg-indigo-100 text-indigo-800 border-indigo-200",
  }[s] || "bg-secondary";
  return <span className={`chip border ${m}`}>{s}</span>;
}

function OrdersTable({ rows, onOpen }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
        <tr>
          <Th>Code</Th>
          <Th>Buyer</Th>
          <Th>Seller</Th>
          <Th>Items</Th>
          <Th>Total</Th>
          <Th>Terms</Th>
          <Th>Status</Th>
          <Th>State signals</Th>
          <Th>Created</Th>
          <Th/>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(o => {
          const cancelled = !!(o.cancellation || o.cancelled_at);
          const hasClaim  = !!o.claim_id;
          const isPrebook = !!o.prebook;
          const hasShipInfo = !!o.shipping_info;
          return (
            <tr key={o.id} data-testid={`adm-ord-${o.code}`}
              className="hover:bg-secondary/40 cursor-pointer transition-colors"
              onClick={() => onOpen?.(o)}>
              <td className="px-5 py-3 mono text-xs">
                <div>{o.code}</div>
                {o.po_number && o.po_number !== o.code && (
                  <div className="text-[10.5px] text-foreground/45 mt-0.5">PO {o.po_number}</div>
                )}
              </td>
              <td className="px-5 py-3 font-medium">{o.buyer_name}</td>
              <td className="px-5 py-3">{o.seller_name}</td>
              <td className="px-5 py-3 text-foreground/55">{o.items?.length || 0}</td>
              <td className="px-5 py-3 display">{money(o.total)}</td>
              <td className="px-5 py-3 uppercase text-xs">{o.terms}</td>
              <td className="px-5 py-3"><StatusChip s={o.status}/></td>
              <td className="px-5 py-3">
                <div className="flex flex-wrap gap-1">
                  {o.source && (
                    <span className="chip border border-border text-foreground/65 text-[10px]">{o.source}</span>
                  )}
                  {isPrebook && (
                    <span className="chip border border-indigo-200 bg-indigo-50 text-indigo-700 text-[10px] inline-flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5"/>prebook
                    </span>
                  )}
                  {cancelled && (
                    <span className="chip border border-rose-200 bg-rose-50 text-rose-700 text-[10px] inline-flex items-center gap-1">
                      <XCircle className="w-2.5 h-2.5"/>cancelled
                    </span>
                  )}
                  {hasClaim && (
                    <span className="chip border border-amber-200 bg-amber-50 text-amber-700 text-[10px] inline-flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5"/>QI claim
                    </span>
                  )}
                  {o.shipping_mode && (
                    <span className="chip border border-border text-foreground/65 text-[10px] inline-flex items-center gap-1" title={`Shipping mode: ${o.shipping_mode}`}>
                      <Truck className="w-2.5 h-2.5"/>{o.shipping_mode.replace("_", "·")}
                    </span>
                  )}
                  {hasShipInfo && (
                    <span className="chip border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] inline-flex items-center gap-1" title="Freight details captured">
                      <FileBadge className="w-2.5 h-2.5"/>freight
                    </span>
                  )}
                </div>
              </td>
              <td className="px-5 py-3 text-xs text-foreground/55">{new Date(o.created_at).toLocaleDateString()}</td>
              <td className="px-5 py-3 text-right">
                <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-widest text-foreground/60">
                  <Sparkles className="w-3 h-3 text-accent"/>360 →
                </span>
              </td>
            </tr>
          );
        })}
        {rows.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-foreground/55">No orders</td></tr>}
      </tbody>
    </table>
  );
}

function InvoicesTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
        <tr><Th>Code</Th><Th>Buyer</Th><Th>Seller</Th><Th>Amount</Th><Th>Terms</Th><Th>Due</Th><Th>Status</Th><Th>Tags</Th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(i => (
          <tr key={i.id} data-testid={`adm-inv-${i.code}`}>
            <td className="px-5 py-3 mono text-xs">{i.code}</td>
            <td className="px-5 py-3 font-medium">{i.buyer_name}</td>
            <td className="px-5 py-3">{i.seller_name}</td>
            <td className="px-5 py-3 display">{money(i.amount)}</td>
            <td className="px-5 py-3 uppercase text-xs">{i.terms}</td>
            <td className="px-5 py-3 text-xs text-foreground/55">{new Date(i.due_date).toLocaleDateString()}</td>
            <td className="px-5 py-3"><StatusChip s={i.status}/></td>
            <td className="px-5 py-3 space-x-1 text-[11px]">
              {i.financed && <span className="chip bg-blue-50 text-blue-700 border border-blue-200">financed</span>}
              {i.insured && <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200">insured</span>}
              {i.payment_intent === "seller_terms" && <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200">free terms</span>}
              {i.extension_terms && <span className="chip bg-indigo-50 text-indigo-700 border border-indigo-200">{i.extension_terms}</span>}
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-foreground/55">No invoices</td></tr>}
      </tbody>
    </table>
  );
}

function SellersTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
        <tr><Th>Seller</Th><Th>Email</Th><Th>Orders</Th><Th>GMV</Th><Th>Paid</Th><Th>Active invoices</Th><Th>Joined</Th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(s => (
          <tr key={s.id} data-testid={`adm-sel-${s.id.slice(0, 6)}`}>
            <td className="px-5 py-3">
              <div className="font-medium">{s.company || s.name}</div>
              <div className="text-[11px] text-foreground/55">{s.name}</div>
            </td>
            <td className="px-5 py-3 text-foreground/70">{s.email}</td>
            <td className="px-5 py-3 mono">{s.orders}</td>
            <td className="px-5 py-3 display">{money(s.gmv)}</td>
            <td className="px-5 py-3 text-foreground/65">{money(s.paid_volume)}</td>
            <td className="px-5 py-3 mono">{s.active_invoices}</td>
            <td className="px-5 py-3 text-xs text-foreground/55">{new Date(s.created_at).toLocaleDateString()}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-foreground/55">No sellers</td></tr>}
      </tbody>
    </table>
  );
}

function DropshipTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
        <tr><Th>Code</Th><Th>Retailer</Th><Th>Supplier</Th><Th>Customer</Th><Th>Retail</Th><Th>Cost</Th><Th>Margin</Th><Th>Status</Th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(d => (
          <tr key={d.id} data-testid={`adm-ds-${d.code}`}>
            <td className="px-5 py-3 mono text-xs">{d.code}</td>
            <td className="px-5 py-3 font-medium">{d.store_name || "—"}</td>
            <td className="px-5 py-3">{d.seller_name}</td>
            <td className="px-5 py-3 text-foreground/65">{d.customer_name}</td>
            <td className="px-5 py-3 display">{money((d.retail_price || 0) * (d.qty || 1))}</td>
            <td className="px-5 py-3 text-foreground/65">{money((d.wholesale_cost || 0) * (d.qty || 1))}</td>
            <td className="px-5 py-3 text-emerald-700 display">{money(d.margin || 0)}</td>
            <td className="px-5 py-3"><StatusChip s={d.status}/></td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-foreground/55">No dropship orders</td></tr>}
      </tbody>
    </table>
  );
}


// ─── Iter-84: Manual orders / Prebook activity / Restocking fees ───────
function ManualOrdersTab({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/orders/manual?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-manual">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <SmKpi label="Manual orders" value={data?.kpis?.total_count ?? 0} icon={Plus}/>
        <SmKpi label="Manual GMV"    value={money(data?.kpis?.total_gmv ?? 0)} icon={DollarSign} tone="emerald"/>
        <SmKpi label="Window"        value={`${sinceDays}d`} icon={Clock}/>
      </div>
      <FilterBar q={q} setQ={setQ} onSearch={load} placeholder="Code / buyer / seller / note"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="manual-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="processing">Processing</option>
            <option value="confirmed">Confirmed</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}/>
      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="manual"
        columns={[
          { l: "Order",   r: (x) => <span className="mono text-[11.5px] underline cursor-pointer" onClick={() => onOpen?.(x)}>{x.code}</span> },
          { l: "Seller",  r: (x) => <Stack top={x.seller_company || x.seller_name} sub={x.seller_email}/> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || x.buyer_name} sub={x.buyer_email}/> },
          { l: "Total",   r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Status",  r: (x) => <StatusChip s={x.status}/> },
          { l: "Note",    r: (x) => <span className="text-[11px] text-foreground/55 max-w-[200px] truncate inline-block">{x.seller_note || "—"}</span> },
          { l: "Created", r: (x) => <span className="text-[11px] text-foreground/55">{x.created_at ? new Date(x.created_at).toLocaleDateString() : "—"}</span> },
          { l: "PDF",     r: (x) => <PdfMenu oid={x.id} iid={null}/> },
        ]}
        empty="No manually-created orders in this window."
      />
    </div>
  );
}


function PrebookActivityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [sinceDays, setSinceDays] = useState(60);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (kind) p.set("event_kind", kind);
      const r = await api.get(`/admin/orders/prebook-activity?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-prebook">
      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <select data-testid="prebook-kind" value={kind} onChange={(e) => setKind(e.target.value)}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="">All event kinds</option>
          <option value="convert">Convert</option>
          <option value="accept">Accept</option>
          <option value="decline">Decline</option>
          <option value="extension_requested">Extension requested</option>
          <option value="extension_decided">Extension decided</option>
          <option value="cancel">Cancel</option>
        </select>
        <select data-testid="prebook-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
          <option value="180">Last 6 months</option>
        </select>
        <button data-testid="prebook-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} events</div>
      </div>

      {data?.by_kind && Object.keys(data.by_kind).length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="prebook-by-kind">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Events by kind</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.by_kind).map(([k, n]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/60 border border-border text-[11px]">
                <span className="font-medium">{k.replace(/_/g, " ")}</span><span className="text-foreground/55">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="prebook"
        columns={[
          { l: "Order",  r: (x) => <span className="mono text-[11.5px]">{x.order_code}</span> },
          { l: "Seller", r: (x) => <Stack top={x.seller_company || x.seller_name} sub={x.seller_email}/> },
          { l: "Buyer",  r: (x) => <Stack top={x.buyer_company || x.buyer_name} sub={x.buyer_email}/> },
          { l: "Total",  r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Event",  r: (x) => <StatusChip s={(x.event || {}).kind || "—"}/> },
          { l: "Note",   r: (x) => <span className="text-[11px] text-foreground/55 max-w-[260px] truncate inline-block">{(x.event || {}).note || (x.event || {}).reason || "—"}</span> },
          { l: "At",     r: (x) => <span className="text-[11px] text-foreground/55">{(x.event || {}).at ? new Date((x.event || {}).at).toLocaleString() : "—"}</span> },
        ]}
        empty="No prebook events in this window."
      />
    </div>
  );
}


function RestockingFeesTab({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minFee, setMinFee] = useState(0);
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays), min_fee_usd: String(minFee) });
      const r = await api.get(`/admin/orders/restocking-fees?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minFee, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-restocking">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <SmKpi label="Fees applied" value={data?.kpis?.total_count ?? 0} icon={DollarSign}/>
        <SmKpi label="Total fee $"  value={money(data?.kpis?.total_fee_usd ?? 0)} icon={AlertTriangle} tone="amber"/>
        <SmKpi label="Window"       value={`${sinceDays}d`} icon={Clock}/>
      </div>
      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <span className="text-[12.5px] text-foreground/65">Min fee ≥</span>
        <select data-testid="rs-min" value={String(minFee)} onChange={(e) => setMinFee(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="0">$0</option>
          <option value="50">$50</option>
          <option value="100">$100</option>
          <option value="500">$500</option>
        </select>
        <select data-testid="rs-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="30">30d</option>
          <option value="90">90d</option>
          <option value="180">6mo</option>
          <option value="365">1y</option>
        </select>
        <button data-testid="rs-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
      </div>
      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="rs"
        columns={[
          { l: "Order",  r: (x) => <span className="mono text-[11.5px] underline cursor-pointer" onClick={() => onOpen?.(x)}>{x.code}</span> },
          { l: "Seller", r: (x) => <Stack top={x.seller_company || x.seller_name} sub={x.seller_email}/> },
          { l: "Buyer",  r: (x) => <Stack top={x.buyer_company || x.buyer_name} sub={x.buyer_email}/> },
          { l: "Total",  r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Fee",    r: (x) => <span className="display tabular-nums text-amber-700">{money(x.restocking_fee)}</span> },
          { l: "Reason", r: (x) => <span className="text-[11px] text-foreground/55 max-w-[260px] truncate inline-block">{x.restocking_fee_reason || "—"}</span> },
          { l: "Status", r: (x) => <StatusChip s={x.status}/> },
        ]}
        empty="No restocking fees applied in this window."
      />
    </div>
  );
}


// ─── iter-95 · Buyer-side P1 visibility tabs ─────────────────────────────
function BuyerCancellationsTab({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [packedOnly, setPackedOnly] = useState("");  // "", "true", "false"
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (q.trim()) p.set("q", q.trim());
      if (packedOnly) p.set("was_packed", packedOnly);
      const r = await api.get(`/admin/orders/buyer-cancellations?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, packedOnly]);

  return (
    <div className="space-y-4" data-testid="adm-buyer-cancels">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmKpi label="Cancellations" value={data?.kpis?.total_count ?? 0} icon={XCircle} tone="rose"/>
        <SmKpi label="Refund total"  value={money(data?.kpis?.total_refund_usd ?? 0)} icon={DollarSign}/>
        <SmKpi label="Restocking $"  value={money(data?.kpis?.total_restocking_usd ?? 0)} icon={AlertTriangle} tone="amber"/>
        <SmKpi label="Packed (fee)"  value={data?.kpis?.packed_count ?? 0} icon={Truck} tone="amber"/>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={load} placeholder="Order code / cancel reason…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="cancel-packed" value={packedOnly} onChange={(e) => setPackedOnly(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All cancellations</option>
            <option value="true">Packed only (fee applied)</option>
            <option value="false">Unpacked only (no fee)</option>
          </select>
        }
        total={data?.total}/>

      {data?.top_buyers && data.top_buyers.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="cancel-top-buyers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">
            Top buyers by cancellation count <span className="text-rose-700">· fraud signal</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_buyers.map((t, i) => (
              <div key={t.buyer_id} data-testid={`cancel-top-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.buyer_company || t.buyer_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.cancellations} · {money(t.total_restocking_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="cancel"
        columns={[
          { l: "Order",     r: (x) => (
              <button onClick={() => onOpen && onOpen({ id: x.id })} className="mono text-[11.5px] hover:underline" data-testid={`cancel-order-${(x.id || "").slice(0, 6)}`}>
                {x.code}
              </button>
            ) },
          { l: "Buyer",     r: (x) => <Stack top={x.buyer_company || "—"} sub={x.buyer_email}/> },
          { l: "Seller",    r: (x) => <Stack top={x.seller_company || "—"} sub={x.seller_email}/> },
          { l: "Total",     r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Refund",    r: (x) => <span className="tabular-nums">{money(x.refund_amount)}</span> },
          { l: "Fee",       r: (x) => x.restocking_fee
              ? <span className="text-rose-700 mono">{money(x.restocking_fee)} <span className="opacity-60 text-[10px]">· {x.restocking_pct || 0}%</span></span>
              : <span className="text-foreground/40 text-[11px]">—</span> },
          { l: "Packed?",   r: (x) => x.was_packed
              ? <span className="chip border bg-amber-50 text-amber-800 border-amber-200 text-[10.5px]">packed</span>
              : <span className="text-foreground/40 text-[11px]">no</span> },
          { l: "Reason",    r: (x) => <span className="text-[11px] text-foreground/55 max-w-[200px] truncate inline-block">{x.cancel_reason || "—"}</span> },
          { l: "Cancelled", r: (x) => <span className="text-[11px] text-foreground/55">{x.cancelled_at ? new Date(x.cancelled_at).toLocaleString() : "—"}</span> },
        ]}
        empty="No buyer cancellations in this window."
      />
    </div>
  );
}


function ShippingChangesTab({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toMode, setToMode] = useState("");
  const [waiverOnly, setWaiverOnly] = useState(false);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (toMode) p.set("to_mode", toMode);
      if (waiverOnly) p.set("waiver_only", "true");
      const r = await api.get(`/admin/orders/shipping-mode-changes?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [toMode, waiverOnly, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-ship-changes">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmKpi label="Total changes"     value={data?.kpis?.total_changes ?? 0} icon={Truck}/>
        <SmKpi label="Buyer-arranged"    value={data?.kpis?.buyer_arranged_count ?? 0} icon={AlertTriangle} tone="amber"/>
        <SmKpi label="Window"            value={`${sinceDays}d`} icon={Clock}/>
        <SmKpi label="Modes"             value={Object.keys(data?.by_to_mode || {}).length} icon={Truck}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <select data-testid="ship-tomode" value={toMode} onChange={(e) => setToMode(e.target.value)}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="">All changes</option>
          <option value="traqo">→ verqo Shipping</option>
          <option value="seller">→ Seller-arranged</option>
          <option value="buyer_arranged">→ Buyer-arranged</option>
        </select>
        <label className="text-[12.5px] flex items-center gap-1.5 px-2 cursor-pointer">
          <input data-testid="ship-waiver-only" type="checkbox" checked={waiverOnly}
            onChange={(e) => setWaiverOnly(e.target.checked)}/> Waiver-evidence only
        </label>
        <select data-testid="ship-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last 1 year</option>
        </select>
        <button data-testid="ship-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} changes</div>
      </div>

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="ship"
        columns={[
          { l: "Order",   r: (x) => (
              <button onClick={() => onOpen && onOpen({ id: x.order_id })} className="mono text-[11.5px] hover:underline">
                {x.order_code}
              </button>
            ) },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || "—"} sub={x.buyer_email}/> },
          { l: "Seller",  r: (x) => <Stack top={x.seller_company || "—"} sub={x.seller_email}/> },
          { l: "From → To", r: (x) => (
              <span className="text-[11.5px]">
                <span className="mono opacity-65">{x.from_mode}</span>
                <span className="mx-1.5 opacity-40">→</span>
                <span className={`mono ${x.to_mode === "buyer_arranged" ? "text-amber-700 font-medium" : ""}`}>{x.to_mode}</span>
              </span>
            ) },
          { l: "By",      r: (x) => <span className="text-[11px] text-foreground/65">{x.changed_by_name || "—"}</span> },
          { l: "Waiver",  r: (x) => x.to_mode === "buyer_arranged"
              ? (x.waiver_accepted
                  ? <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]" data-testid="ship-waiver-yes">accepted</span>
                  : <span className="chip border bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]" data-testid="ship-waiver-no">missing</span>)
              : <span className="text-foreground/40 text-[11px]">n/a</span> },
          { l: "Evidence IP", r: (x) => x.waiver_ip
              ? <span className="mono text-[10.5px] text-foreground/65">{x.waiver_ip}</span>
              : <span className="text-foreground/40 text-[11px]">—</span> },
          { l: "Changed",  r: (x) => <span className="text-[11px] text-foreground/55">{x.changed_at ? new Date(x.changed_at).toLocaleString() : "—"}</span> },
        ]}
        empty="No shipping-mode changes in this window."
      />
    </div>
  );
}


function ExternalPosTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/buyer-hub/external-pos?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-ext-pos">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmKpi label="External POs"     value={data?.kpis?.total_count ?? 0} icon={FileText}/>
        <SmKpi label="Total value"      value={money(data?.kpis?.total_value_usd ?? 0)} icon={DollarSign} tone="emerald"/>
        <SmKpi label="Claimed by seller" value={data?.kpis?.claimed_count ?? 0} icon={CheckCircle2} tone="emerald"/>
        <SmKpi label="Window"           value={`${sinceDays}d`} icon={Clock}/>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={load} placeholder="PO# / supplier name / email…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="ext-pos-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="tracked">Tracked</option>
            <option value="sent">Sent</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="shipped">Shipped</option>
            <option value="invoiced">Invoiced</option>
            <option value="paid">Paid</option>
          </select>
        }
        counts={data?.by_status}
        total={data?.total}/>

      {data?.top_buyers && data.top_buyers.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="ext-pos-top-buyers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">
            Top buyers by external-PO volume <span className="text-emerald-700">· growth funnel</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_buyers.map((t, i) => (
              <div key={t.buyer_id} data-testid={`ext-pos-top-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.buyer_company || t.buyer_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.po_count} POs · {money(t.total_value_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="ext-pos"
        columns={[
          { l: "PO #",     r: (x) => <span className="mono text-[11.5px]">{x.po_number || x.id?.slice(0, 8)}</span> },
          { l: "Buyer",    r: (x) => <Stack top={x.buyer_company || x.buyer_name || "—"} sub={x.buyer_id?.slice(0, 8)}/> },
          { l: "Supplier", r: (x) => <Stack top={x.supplier_name || "—"} sub={x.supplier_email}/> },
          { l: "Total",    r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Terms",    r: (x) => <span className="text-[11px] uppercase mono">{x.terms || "—"}</span> },
          { l: "Status",   r: (x) => <StatusChip s={x.status}/> },
          { l: "Claimed?", r: (x) => x.claimed_seller_id
              ? <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]">claimed</span>
              : <span className="text-foreground/40 text-[11px]">no</span> },
          { l: "Created",  r: (x) => <span className="text-[11px] text-foreground/55">{x.created_at ? new Date(x.created_at).toLocaleDateString() : "—"}</span> },
        ]}
        empty="No external POs in this window."
      />
    </div>
  );
}


function ExternalInvoicesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [financedOnly, setFinancedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (financedOnly) p.set("financed_only", "true");
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/buyer-hub/external-invoices?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, financedOnly, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-ext-invoices">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmKpi label="External invoices"   value={data?.kpis?.total_count ?? 0} icon={FileBadge}/>
        <SmKpi label="Total amount"        value={money(data?.kpis?.total_amount_usd ?? 0)} icon={DollarSign}/>
        <SmKpi label="Financed (3%)"       value={data?.kpis?.financed_count ?? 0} icon={Sparkles} tone="emerald"/>
        <SmKpi label="Hub revenue (verqo)" value={money(data?.kpis?.financing_fee_revenue_usd ?? 0)} icon={DollarSign} tone="emerald"/>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={load} placeholder="invoice code / supplier name…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="ext-inv-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="financed">Financed</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
            <label className="text-[12.5px] flex items-center gap-1.5 px-2 cursor-pointer">
              <input data-testid="ext-inv-financed-only" type="checkbox" checked={financedOnly}
                onChange={(e) => setFinancedOnly(e.target.checked)}/> Financed only
            </label>
          </>
        }
        counts={data?.by_status}
        total={data?.total}/>

      {data?.top_buyers_by_revenue && data.top_buyers_by_revenue.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="ext-inv-top-buyers-revenue">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">
            Top buyers by financing-fee revenue <span className="text-emerald-700">· retention/expansion target</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_buyers_by_revenue.map((t, i) => (
              <div key={t.buyer_id} data-testid={`ext-inv-top-rev-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.buyer_company || t.buyer_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.financed_count}/{t.invoice_count} · {money(t.financing_fee_revenue_usd)} revenue</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="ext-inv"
        columns={[
          { l: "Invoice",   r: (x) => <span className="mono text-[11.5px]">{x.code || x.number || x.id?.slice(0, 8)}</span> },
          { l: "Buyer",     r: (x) => <Stack top={x.buyer_company || "—"} sub={x.buyer_email}/> },
          { l: "Supplier",  r: (x) => <Stack top={x.supplier_name || "—"} sub={x.supplier_email}/> },
          { l: "Amount",    r: (x) => <span className="display tabular-nums">{money(x.amount)}</span> },
          { l: "Status",    r: (x) => <StatusChip s={x.status}/> },
          { l: "Fin. fee",  r: (x) => x.financing_fee
              ? <span className="text-emerald-700 mono">{money(x.financing_fee)}</span>
              : <span className="text-foreground/40 text-[11px]">—</span> },
          { l: "Fin. at",   r: (x) => <span className="text-[11px] text-foreground/55">{x.financing_accepted_at ? new Date(x.financing_accepted_at).toLocaleDateString() : "—"}</span> },
          { l: "Created",   r: (x) => <span className="text-[11px] text-foreground/55">{x.created_at ? new Date(x.created_at).toLocaleDateString() : "—"}</span> },
        ]}
        empty="No external invoices in this window."
      />
    </div>
  );
}



// ─── iter-97 · PO Lifecycle + Supplier-Claims visibility ───────────────
const PO_EVENT_TONE = {
  acknowledged:     "bg-violet-50 text-violet-700 border-violet-200",
  tracking:         "bg-blue-50 text-blue-700 border-blue-200",
  invoice_uploaded: "bg-amber-50 text-amber-800 border-amber-200",
  paid:             "bg-emerald-50 text-emerald-700 border-emerald-200",
  claimed:          "bg-pink-50 text-pink-700 border-pink-200",
};
function PoEventPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${PO_EVENT_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {(v || "—").replace(/_/g, " ")}
    </span>
  );
}

function PoLifecycleTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (kind) p.set("event_kind", kind);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/buyer-hub/po-lifecycle-events?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-po-lifecycle">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SmKpi label="Total events" value={data?.kpis?.total_events ?? 0} icon={HistoryIcon}/>
        <SmKpi label="Acknowledged" value={data?.by_kind?.acknowledged ?? 0} tone="emerald" icon={CheckCircle2}/>
        <SmKpi label="Tracking"     value={data?.by_kind?.tracking ?? 0} icon={Truck}/>
        <SmKpi label="Paid"         value={data?.by_kind?.paid ?? 0} tone="emerald" icon={DollarSign}/>
        <SmKpi label="Claimed"      value={data?.by_kind?.claimed ?? 0} tone="amber" icon={Sparkles}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <select data-testid="po-lc-kind" value={kind} onChange={(e) => setKind(e.target.value)}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="">All event kinds</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="tracking">Tracking added</option>
          <option value="invoice_uploaded">Invoice uploaded</option>
          <option value="paid">Marked paid</option>
          <option value="claimed">Supplier claimed</option>
        </select>
        <select data-testid="po-lc-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
        </select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="po-lc-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="po_number / supplier name…" className="h-9 pl-8 text-[13px] w-[260px]"/>
        </div>
        <button data-testid="po-lc-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} events</div>
      </div>

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="po-lc"
        columns={[
          { l: "Event",    r: (x) => <PoEventPill v={x.event_kind}/> },
          { l: "PO #",     r: (x) => <span className="mono text-[11.5px]">{x.po_number || x.po_id?.slice(0, 8) || "—"}</span> },
          { l: "Buyer",    r: (x) => <Stack top={x.buyer_company || "—"} sub={x.buyer_email}/> },
          { l: "Supplier", r: (x) => <Stack top={x.supplier_name || "—"} sub={x.supplier_email}/> },
          { l: "Total",    r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "PO Status", r: (x) => x.po_status ? <StatusChip s={x.po_status}/> : <span className="text-foreground/40 text-[11px]">—</span> },
          { l: "Note",     r: (x) => <span className="text-[11px] text-foreground/55 max-w-[200px] truncate inline-block">{x.note || "—"}</span> },
          { l: "At",       r: (x) => <span className="text-[11px] text-foreground/55">{x.at ? new Date(x.at).toLocaleString() : "—"}</span> },
        ]}
        empty="No PO lifecycle events in this window."
      />
    </div>
  );
}


function SupplierClaimsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      const r = await api.get(`/admin/buyer-hub/supplier-claims?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-supplier-claims">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmKpi label="Suppliers claimed"  value={data?.kpis?.total_claims ?? 0} icon={Sparkles} tone="emerald"/>
        <SmKpi label="Eligible POs"       value={data?.kpis?.eligible_pos ?? 0} icon={FileText}/>
        <SmKpi label="Conversion rate"    value={`${data?.kpis?.conversion_pct ?? 0}%`} tone="amber" icon={CheckCircle2}/>
        <SmKpi label="Window"             value={`${sinceDays}d`} icon={Clock}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <select data-testid="sc-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last 1 year</option>
        </select>
        <button data-testid="sc-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} claims</div>
      </div>

      {data?.top_buyers && data.top_buyers.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="sc-top-buyers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">
            Top buyers driving supplier signups <span className="text-emerald-700">· viral acquisition · incentive candidates</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_buyers.map((t, i) => (
              <div key={t.buyer_id} data-testid={`sc-top-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.buyer_company || t.buyer_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.claim_count} claims · {money(t.total_value_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SimpleTable
        loading={loading} rows={data?.rows || []} testid="sc"
        columns={[
          { l: "Buyer",         r: (x) => <Stack top={x.buyer_company || x.buyer_name || "—"} sub={x.buyer_email}/> },
          { l: "Claimed seller", r: (x) => <Stack top={x.claimed_seller_company || x.supplier_name || "—"}
              sub={x.claimed_seller_email || x.supplier_email}/> },
          { l: "PO #",          r: (x) => <span className="mono text-[11.5px]">{x.po_number || x.id?.slice(0, 8)}</span> },
          { l: "Total",         r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Views",         r: (x) => <span className="mono text-[11px] text-foreground/65">{x.public_view_count ?? 0}</span> },
          { l: "First view",    r: (x) => <span className="text-[11px] text-foreground/55">{x.first_public_view_at ? new Date(x.first_public_view_at).toLocaleDateString() : "—"}</span> },
          { l: "Claimed at",    r: (x) => <span className="text-[11px] text-emerald-700 font-medium">{x.claimed_at ? new Date(x.claimed_at).toLocaleString() : "—"}</span> },
        ]}
        empty="No supplier claims in this window."
      />
    </div>
  );
}


// ─── Shared mini-kit ───────────────────────────────────────────────────
function SmKpi({ label, value, icon: Icon, tone }) {
  const tones = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  return (
    <div className="rounded-2xl bg-white border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/40"/>}
      </div>
      <div className={`display text-2xl mt-1.5 ${tones[tone] || ""}`}>{value}</div>
    </div>
  );
}

function FilterBar({ q, setQ, onSearch, placeholder, sinceDays, setSinceDays, extra, counts, total }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
        <Input data-testid="cmc-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          placeholder={placeholder} className="h-9 pl-8 text-[13px] w-[260px]"/>
      </div>
      {extra}
      <select data-testid="cmc-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
        className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="180">Last 6 months</option>
        <option value="365">Last 1 year</option>
        <option value="730">Last 2 years</option>
      </select>
      <button data-testid="cmc-refresh" onClick={onSearch}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
        <RefreshCw size={13}/> Refresh
      </button>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-foreground/55">
        <span>{total ?? 0} rows</span>
        {counts && Object.keys(counts).length > 0 && (
          <span className="flex items-center gap-1.5">
            {Object.entries(counts).slice(0, 4).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border">
                <span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

function SimpleTable({ loading, rows, columns, empty, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`${testid}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-2.5 font-medium">{c.l}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55" data-testid={`${testid}-empty`}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || r.order_id || i} data-testid={`${testid}-row-${i}`} className="hover:bg-secondary/20">
              {columns.map((c, j) => <td key={j} className="px-4 py-3">{c.r(r, i)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stack({ top, sub }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] font-medium">{top || "—"}</div>
      <div className="text-[11px] text-foreground/55">{sub || ""}</div>
    </div>
  );
}

// Bulk actions toolbar (iter-84) — admin can bulk-confirm or bulk-remind
// across multiple orders for support workflows when sellers are
// unresponsive. Paste comma- or newline-separated order IDs.
function BulkActionsBar() {
  const [open, setOpen] = useState(false);
  const [orderIds, setOrderIds] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null); // "confirm" | "remind" | null

  const idList = () =>
    orderIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);

  const run = async (kind) => {
    const ids = idList();
    if (!ids.length) { toast.error("Add at least one order ID"); return; }
    if (reason.trim().length < 3) { toast.error("Reason must be ≥ 3 characters"); return; }
    setBusy(kind);
    try {
      const path = kind === "confirm" ? "/admin/orders/bulk-confirm" : "/admin/invoices/bulk-remind";
      const { data } = await api.post(path, { order_ids: ids, reason: reason.trim() });
      toast.success(`${kind === "confirm" ? "Confirmed" : "Reminded"}: ${data.count_ok} · Skipped: ${data.count_skip}`);
      if (data.count_skip > 0) {
        const reasons = (data.skipped || []).map(s => `${s.order_id?.slice(0, 8)}: ${s.reason}`).join("\n");
        toast.message("Skipped details", { description: reasons.slice(0, 600) });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally { setBusy(null); }
  };

  if (!open) {
    return (
      <div className="mt-3" data-testid="adm-bulk-bar-collapsed">
        <button onClick={() => setOpen(true)} data-testid="bulk-bar-open"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-full px-3 py-1.5 hover:bg-secondary">
          <CheckCircle2 size={13}/> Bulk actions
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-2xl bg-white border border-border p-4 space-y-3" data-testid="adm-bulk-bar">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium">Bulk actions</div>
        <button onClick={() => setOpen(false)} data-testid="bulk-bar-close"
          className="text-[11px] text-foreground/50 hover:text-foreground">Hide</button>
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-foreground/55 mb-1">Order IDs (comma or newline separated)</div>
        <textarea data-testid="bulk-ids" value={orderIds} onChange={(e) => setOrderIds(e.target.value)}
          rows={3}
          placeholder="ord-abc123, ord-def456&#10;ord-xyz789"
          className="w-full text-[12.5px] mono rounded-md border border-border bg-white px-3 py-2"/>
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-foreground/55 mb-1">Reason (≥3 chars · audit-chained)</div>
        <Input data-testid="bulk-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Seller unresponsive — confirming on their behalf per support ticket #STK-1234"
          className="h-9 text-[13px]"/>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="bulk-confirm-btn" onClick={() => run("confirm")} disabled={busy !== null}
          className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 px-3 text-xs">
          <CheckCircle2 size={13} className="mr-1.5"/>{busy === "confirm" ? "Confirming…" : `Bulk confirm (${idList().length})`}
        </Button>
        <Button data-testid="bulk-remind-btn" variant="outline" onClick={() => run("remind")} disabled={busy !== null}
          className="rounded-full h-9 px-3 text-xs">
          <Bell size={13} className="mr-1.5"/>{busy === "remind" ? "Sending…" : `Bulk remind (${idList().length})`}
        </Button>
        <div className="ml-auto text-[10.5px] text-foreground/55">
          Every bulk action is hash-chained in admin_overrides.
        </div>
      </div>
    </div>
  );
}


// Per-row PDF download menu (PO + Invoice + Pack-list). Admin already has
// bypass on these existing endpoints so no special auth needed.
function PdfMenu({ oid, iid }) {
  const dl = async (url, fname) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };
  return (
    <div className="flex items-center gap-1">
      <button title="Download PO" onClick={() => dl(`/orders/${oid}/po.pdf`, `po-${oid.slice(0, 8)}.pdf`)}
        data-testid={`pdf-po-${oid.slice(0, 6)}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10.5px] hover:bg-secondary">
        <FileBadge size={11}/>PO
      </button>
      <button title="Download Pack-list" onClick={() => dl(`/orders/${oid}/pack-list/pdf`, `pack-${oid.slice(0, 8)}.pdf`)}
        data-testid={`pdf-pack-${oid.slice(0, 6)}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10.5px] hover:bg-secondary">
        <FileText size={11}/>Pack
      </button>
      {iid && (
        <button title="Download Invoice" onClick={() => dl(`/invoices/${iid}/pdf`, `invoice-${iid.slice(0, 8)}.pdf`)}
          data-testid={`pdf-inv-${iid.slice(0, 6)}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10.5px] hover:bg-secondary">
          <Download size={11}/>Inv
        </button>
      )}
    </div>
  );
}
